/* FILE: assets/sim_slideshow_autoplay_driver.js
   Simbiosis per-slide autoplay driver (non-legacy, section-scoped)

   - Theme slideshow autoplay should be disabled in the section render.
   - Per-slide durations up to 45s (from [data-sim-slide-meta][data-slide-duration]).
   - Timer resets when the active slide changes (dots/arrows/swipe/click).
   - Pause/resume controlled via custom events (dispatched by mask controller):
     - "sim:autoplay:pause"
     - "sim:autoplay:play"

   Improvements vs older version:
   - Avoids "double scheduling" (tick + aria-hidden observer) causing early advances.
   - Remembers remaining time on pause, resumes from remaining (does not reset).
*/

(() => {
  const ROOT_SELECTOR = "[data-sim-slideshow]";

  const clamp = (v, min, max) => (v < min ? min : v > max ? max : v);
  const num = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  function getSlides(root) {
    return Array.from(root.querySelectorAll("slideshow-slide:not([hidden])"));
  }

  function getActiveIndex(slides) {
    const i = slides.findIndex((s) => s.getAttribute("aria-hidden") === "false");
    return i >= 0 ? i : 0;
  }

  function getDuration(slide, fallbackSec) {
    const meta = slide?.querySelector("[data-sim-slide-meta]");
    return clamp(num(meta?.dataset?.slideDuration, fallbackSec), 1, 45);
  }

  function isEnabled(root) {
    return String(root.dataset.simAutoplayEnabled) === "true";
  }

  function defaultSec(root) {
    return clamp(num(root.dataset.simAutoplayDefault, 4), 1, 45);
  }

  function selectSlide(slide) {
    try {
      slide.click();
    } catch (e) {
      // no-op
    }
  }

  class Driver {
    constructor(root) {
      this.root = root;

      this.timer = 0;
      this.deadlineMs = 0;
      this.remainingMs = 0;

      this.paused = false;

      this.lastActiveIndex = -1;
      this.ignoreChangeUntil = 0;

      this._onChange = this._onChange.bind(this);
      this._tick = this._tick.bind(this);
      this._onPause = this._onPause.bind(this);
      this._onPlay = this._onPlay.bind(this);

      this._init();
    }

    _clear() {
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = 0;
      }
      this.deadlineMs = 0;
    }

    _schedule(ms) {
      this._clear();
      if (this.paused) return;
      if (!isEnabled(this.root)) return;

      const slides = getSlides(this.root);
      if (slides.length <= 1) return;

      const safeMs = Math.max(0, Number(ms) || 0);
      this.remainingMs = safeMs;
      this.deadlineMs = Date.now() + safeMs;

      this.timer = setTimeout(this._tick, safeMs);
    }

    _scheduleForActive(fullReset = false) {
      if (this.paused) return;
      if (!isEnabled(this.root)) return;

      const slides = getSlides(this.root);
      if (slides.length <= 1) {
        this._clear();
        return;
      }

      const idx = getActiveIndex(slides);
      this.lastActiveIndex = idx;

      const sec = getDuration(slides[idx], defaultSec(this.root));
      const ms = sec * 1000;

      if (fullReset) {
        this._schedule(ms);
        return;
      }

      if (!this.deadlineMs) {
        this._schedule(ms);
        return;
      }

      // Otherwise leave existing schedule.
    }

    _tick() {
      if (this.paused) return;
      if (!isEnabled(this.root)) return;

      const slides = getSlides(this.root);
      if (slides.length <= 1) return;

      const idx = getActiveIndex(slides);
      const next = slides[(idx + 1) % slides.length];

      this.ignoreChangeUntil = Date.now() + 600;
      selectSlide(next);

      this._clear();
    }

    _onChange() {
      if (Date.now() < this.ignoreChangeUntil) {
        clearTimeout(this._changeDebounce);
        this._changeDebounce = setTimeout(() => {
          if (this.paused) return;
          if (!isEnabled(this.root)) return;
          this._scheduleForActive(true);
        }, 80);
        return;
      }

      this._scheduleForActive(true);
    }

    _onPause() {
      if (this.paused) return;
      this.paused = true;

      // capture remaining
      const now = Date.now();

      if (!this.deadlineMs) {
        // nothing scheduled; keep remainingMs as-is
        this.remainingMs = Math.max(0, this.remainingMs || 0);
      } else {
        const remaining = this.deadlineMs - now;
        this.remainingMs = Math.max(0, remaining);
      }

      this._clear();
    }

    _onPlay() {
      if (!this.paused) return;
      this.paused = false;

      const slides = getSlides(this.root);
      if (slides.length <= 1) return;

      const idx = getActiveIndex(slides);
      const fullMs = getDuration(slides[idx], defaultSec(this.root)) * 1000;

      const ms = this.remainingMs > 0 ? this.remainingMs : fullMs;
      this._schedule(ms);
    }

    _init() {
      try {
        this.mo = new MutationObserver(this._onChange);
        this.mo.observe(this.root, {
          subtree: true,
          attributes: true,
          attributeFilter: ["aria-hidden"]
        });
      } catch (e) {}

      this.root.addEventListener("slideshow:change", this._onChange);
      this.root.addEventListener("sim:autoplay:pause", this._onPause);
      this.root.addEventListener("sim:autoplay:play", this._onPlay);

      this._scheduleForActive(true);
    }

    destroy() {
      this._clear();
      if (this._changeDebounce) clearTimeout(this._changeDebounce);
      if (this.mo) this.mo.disconnect();
      this.root.removeEventListener("slideshow:change", this._onChange);
      this.root.removeEventListener("sim:autoplay:pause", this._onPause);
      this.root.removeEventListener("sim:autoplay:play", this._onPlay);
    }
  }

  const instances = new Map();

  function mount(root) {
    if (!root || instances.has(root)) return;
    instances.set(root, new Driver(root));
  }

  function unmount(root) {
    const inst = instances.get(root);
    if (!inst) return;
    inst.destroy();
    instances.delete(root);
  }

  function scan(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(mount);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => scan());
  } else {
    scan();
  }

  document.addEventListener("shopify:section:load", (e) => scan(e.target || document));
  document.addEventListener("shopify:section:unload", (e) => {
    (e.target || document).querySelectorAll(ROOT_SELECTOR).forEach(unmount);
  });
})();
